"use client";

import {
  type UIEvent,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";

const BOTTOM_THRESHOLD_PX = 32;

type ChatScrollFollowOptions = {
  active: boolean;
  contentVersion: string;
};

/**
 * 让聊天时间线仅在用户原本停留于底部时跟随新内容。
 * 输入：聊天是否启用及可见内容版本；输出：滚动容器 ref 与 scroll 处理器。
 * 用户离开底部后必须保留阅读位置，直到其主动回到底部，AI 更新不得抢走视口。
 */
export function useChatScrollFollow({
  active,
  contentVersion,
}: ChatScrollFollowOptions) {
  const containerRef = useRef<HTMLElement | null>(null);
  const shouldFollowBottomRef = useRef(true);
  const setContainer = useCallback((container: HTMLElement | null) => {
    containerRef.current = container;
  }, []);

  const onScroll = useCallback((event: UIEvent<HTMLElement>) => {
    const container = event.currentTarget;
    const distanceFromBottom =
      container.scrollHeight - container.clientHeight - container.scrollTop;
    shouldFollowBottomRef.current = distanceFromBottom <= BOTTOM_THRESHOLD_PX;
  }, []);

  useLayoutEffect(() => {
    if (!active) {
      // 离开聊天页后重置，下一次进入新对话默认展示最新内容。
      shouldFollowBottomRef.current = true;
      return;
    }
    const container = containerRef.current;
    if (!container || !shouldFollowBottomRef.current) return;
    // 直接设置位置，避免连续的 AI 状态更新产生长时间平滑滚动和视口追逐。
    container.scrollTop = container.scrollHeight;
  }, [active, contentVersion]);

  return { setContainer, onScroll };
}
